import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  TextField,
  InputLabel,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { Fragment } from 'react';
import { resolveOptionDisplayLabel } from '../../../utils/config-localization';
import { toSelectFormString } from '../merge-form-select';

const getOptionDisplayText = (opt, formatDisplayValue) => {
  if (opt && typeof opt === 'object') {
    return resolveOptionDisplayLabel(opt) ||
      (formatDisplayValue ? formatDisplayValue(opt?.value) : opt?.value);
  }
  return formatDisplayValue ? formatDisplayValue(opt) : opt;
};

const ReadOnlySelect = ({
  value,
  options = [],
  formatDisplayValue,
  selectSx,
}) => {
  const normalizedValue = toSelectFormString(value);
  const safeOptions = options?.length
    ? options
    : normalizedValue
      ? [{ value: normalizedValue, label: normalizedValue }]
      : [];

  return (
    <Select
      disabled
      sx={{ width: '100%', ...selectSx }}
      size="small"
      fullWidth
      value={normalizedValue}
      renderValue={(selected) =>
        formatDisplayValue ? formatDisplayValue(selected) : selected
      }
    >
      {safeOptions.map((opt) => (
        <MenuItem key={String(opt?.value)} value={toSelectFormString(opt?.value)}>
          {getOptionDisplayText(opt, formatDisplayValue)}
        </MenuItem>
      ))}
    </Select>
  );
};

const ControlledAutocomplete = ({
  options = [],
  renderInput,
  getOptionLabel,
  control,
  defaultValue,
  name,
  renderOption,
  formatDisplayValue,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, ...props } }) => {
        const selectedOption =
          options?.find((opt) => String(opt?.value) === String(value)) ?? null;

        return (
          <Autocomplete
            fullWidth
            options={options?.filter(
              (fi) =>
                fi?.value !== null &&
                fi?.value !== undefined &&
                fi?.value !== '',
            )}
            getOptionLabel={getOptionLabel}
            freeSolo
            renderOption={renderOption}
            renderInput={renderInput}
            onChange={(_, data) => onChange(data?.value ?? data ?? '')}
            autoSelect
            value={
              selectedOption ??
              (value != null && value !== ''
                ? {
                    value,
                    label: formatDisplayValue
                      ? formatDisplayValue(value)
                      : String(value),
                  }
                : null)
            }
            isOptionEqualToValue={(opt, val) =>
              String(opt?.value) === String(val?.value ?? val)
            }
            {...props}
          />
        );
      }}
    />
  );
};

const ReactHookFormSelect = ({
  name,
  label,
  control,
  children,
  disabled,
  selectSx,
  formatDisplayValue,
  options = [],
  defaultValue = '',
}) => {
  const labelId = `${name}-label`;
  const normalizedDefault = toSelectFormString(defaultValue);

  return (
    <Fragment>
      {label ? <InputLabel id={labelId}>{label}</InputLabel> : null}
      <Controller
        name={name}
        control={control}
        defaultValue={normalizedDefault}
        render={({ field: { value, onChange, onBlur, ref } }) => {
          const normalizedValue = toSelectFormString(value);

          return (
            <Select
              disabled={disabled}
              sx={{ width: '100%', ...selectSx }}
              size="small"
              fullWidth
              displayEmpty
              inputRef={ref}
              onBlur={onBlur}
              onChange={(event) => {
                onChange(toSelectFormString(event.target.value));
              }}
              value={normalizedValue}
              labelId={labelId}
              label={label}
              renderValue={(selected) => {
                const selectedOption = options.find(
                  (opt) => toSelectFormString(opt?.value) === toSelectFormString(selected),
                );
                return selectedOption
                  ? getOptionDisplayText(selectedOption, formatDisplayValue)
                  : (formatDisplayValue
                    ? formatDisplayValue(selected)
                    : selected);
              }}
            >
              {children}
            </Select>
          );
        }}
      />
    </Fragment>
  );
};

const mergeInputSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    borderRadius: '8px',
  },
};

const getInputByType = ({ input, control }) => {
  const fieldSx = { ...mergeInputSx, ...(input?.inputSx || {}) };
  const displayValue = input?.formatDisplayValue
    ? input.formatDisplayValue(input.fieldValue)
    : input.fieldValue;

  if (input?.readOnly) {
    if (input?.type === 'select') {
      return (
        <ReadOnlySelect
          value={input.fieldValue}
          options={input.options}
          formatDisplayValue={input.formatDisplayValue}
          selectSx={fieldSx}
        />
      );
    }

    return (
      <TextField
        sx={fieldSx}
        size="small"
        fullWidth
        disabled
        value={displayValue ?? ''}
      />
    );
  }

  if (input?.type === 'autocomplete') {
    const formattedOptions = input?.options?.map((opt) => ({
      ...opt,
      value: toSelectFormString(opt?.value),
      label: getOptionDisplayText(opt, input?.formatDisplayValue),
    }));

    return (
      <ControlledAutocomplete
        control={control}
        name={input?.registerName}
        defaultValue={input.defaultValue}
        options={formattedOptions}
        formatDisplayValue={input?.formatDisplayValue}
        getOptionLabel={(option) => {
          if (typeof option === 'string' || typeof option === 'number') {
            return input?.formatDisplayValue
              ? input.formatDisplayValue(option)
              : String(option);
          }
          return (
            getOptionDisplayText(option, input?.formatDisplayValue)?.toString() ||
            ''
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            fullWidth
            sx={fieldSx}
          />
        )}
      />
    );
  }

  if (input?.type === 'select') {
    const currentValue = toSelectFormString(input?.defaultValue ?? input?.fieldValue);
    const optionValues = new Set(
      (input?.options ?? []).map((opt) => toSelectFormString(opt?.value)),
    );
    const needsFallbackOption =
      currentValue !== '' && !optionValues.has(currentValue);

    return (
      <ReactHookFormSelect
        name={input?.registerName}
        control={control}
        disabled={input?.disabled}
        selectSx={fieldSx}
        formatDisplayValue={input?.formatDisplayValue}
        options={input?.options}
        defaultValue={input?.defaultValue}
      >
        {input?.options?.map((opt) => (
          <MenuItem
            key={`${input.registerName}-${opt?.value}`}
            value={toSelectFormString(opt?.value)}
          >
            {getOptionDisplayText(opt, input?.formatDisplayValue)}
          </MenuItem>
        ))}
        {needsFallbackOption ? (
          <MenuItem key={`${input.registerName}-current`} value={currentValue}>
            {input?.formatDisplayValue
              ? input.formatDisplayValue(currentValue)
              : currentValue}
          </MenuItem>
        ) : null}
      </ReactHookFormSelect>
    );
  }

  return (
    <Controller
      name={input?.registerName}
      control={control}
      defaultValue={input.defaultValue ?? ''}
      render={({ field: { value } }) => (
        <TextField
          sx={fieldSx}
          size="small"
          fullWidth
          disabled
          value={
            (input?.formatDisplayValue
              ? input.formatDisplayValue(value)
              : value) ?? ''
          }
        />
      )}
    />
  );
};

const mergeFormCellSx = {
  minWidth: 0,
  px: 1.5,
  py: 1.25,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
};

export const registerInputs = ({ inputs, control }) => {
  return (
    <Fragment>
      {inputs?.map((input) => (
        <Box key={input?.registerName} sx={mergeFormCellSx}>
          {getInputByType({ input, control })}
        </Box>
      ))}
    </Fragment>
  );
};
